import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "../components/Layout";
import { api, formatINR, blurOnWheel } from "../lib/api";
import { useAuth, useHasPermission } from "../context/AuthContext";
import { Plus, Check, Flame, Search, X, Layers, Paperclip, AlertTriangle, ExternalLink, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

export default function NewRequest() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Phase F3 — confidential toggle rendered only for view_confidential holders.
  const canConfidential = useHasPermission("view_confidential");
  const [meta, setMeta] = useState({ sites: [], categories: [], thresholds: { small: 10000, medium: 50000 } });
  const [vendors, setVendors] = useState([]);
  const [site, setSite] = useState(user?.assigned_sites?.[0] || user?.site || "");
  const [category, setCategory] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [bulk, setBulk] = useState(false);
  const [amount, setAmount] = useState("");
  const [items, setItems] = useState([{ description: "", amount: "" }]);
  const [requestedMode, setRequestedMode] = useState("");
  // v2.12 — employee_name is required when requestedMode === "Employee Account"
  const [employeeName, setEmployeeName] = useState("");
  const [notes, setNotes] = useState("");
  const [purchaseTime, setPurchaseTime] = useState("");
  const [supportingFiles, setSupportingFiles] = useState([]);
  const [vendorOpen, setVendorOpen] = useState(false);
  // Phase F3 — confidential flag on the request.
  const [confidential, setConfidential] = useState(false);
  const [vendorSearch, setVendorSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [createVendorOpen, setCreateVendorOpen] = useState(false);
  const [newVendorName, setNewVendorName] = useState("");
  const fileRef = useRef(null);
  // Phase F2 — duplicate scan (warn-only, never blocks).
  const [dupCheck, setDupCheck] = useState({ matches: [], excluded: false, loading: false });
  const dupTimerRef = useRef(null);
  // Phase C0 — cash-expense auto-approve rules (server-derived UX preview).
  const [cashRules, setCashRules] = useState({ allowed_categories: [], amount_cap: 15000, has_permission: false });

  useEffect(() => {
    api.get("/sites").then((r) => {
      setMeta(r.data);
      // If user is single-site (not all_sites and only one assigned), lock to that site.
      if (!r.data?.all_sites && (r.data?.sites || []).length === 1) {
        setSite(r.data.sites[0]);
      }
    });
    api.get("/vendors").then((r) => setVendors(r.data));
    // Only fetch rules if user might qualify — endpoint returns has_permission=false
    // for everyone else which suppresses the preview cleanly.
    api.get("/cash-expense/rules").then((r) => setCashRules(r.data)).catch(() => {});
  }, []);

  // Single-site users see a locked readonly site display, not a dropdown.
  const isSingleSite = !user?.all_sites && (meta.sites?.length || 0) <= 1;

  const totalBulk = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const effectiveAmount = bulk ? totalBulk : Number(amount || 0);

  // Phase F2 — debounced duplicate scan once vendor + category + amount are set.
  // Skipped entirely when the confidential toggle is on (management spend).
  useEffect(() => {
    if (dupTimerRef.current) clearTimeout(dupTimerRef.current);
    if (!vendorId || !category || !effectiveAmount || effectiveAmount <= 0 || confidential) {
      setDupCheck({ matches: [], excluded: false, loading: false });
      return;
    }
    setDupCheck((s) => ({ ...s, loading: true }));
    dupTimerRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          vendor_id: vendorId,
          category,
          amount: String(effectiveAmount),
        });
        if (site) params.set("site", site);
        const res = await api.get(`/requests/check-duplicates?${params.toString()}`);
        setDupCheck({
          matches: res.data?.matches || [],
          excluded: !!res.data?.excluded_category,
          loading: false,
        });
      } catch {
        setDupCheck({ matches: [], excluded: false, loading: false });
      }
    }, 450);
    return () => { if (dupTimerRef.current) clearTimeout(dupTimerRef.current); };
  }, [vendorId, category, effectiveAmount, site, confidential]);

  const tier = (n) => {
    const a = Number(n || 0);
    if (a < meta.thresholds.small) return { label: "SMALL", color: "text-slate-500", sla: "NO SLA" };
    if (a < meta.thresholds.medium) return { label: "MEDIUM", color: "text-amber-600", sla: "2H SLA" };
    return { label: "HIGH VALUE", color: "text-red-600", sla: "6H SLA" };
  };

  const addItem = () => setItems([...items, { description: "", amount: "" }]);
  const removeItem = (idx) => setItems(items.length === 1 ? items : items.filter((_, i) => i !== idx));
  const updateItem = (idx, key, value) => {
    const copy = [...items];
    copy[idx][key] = value;
    setItems(copy);
  };

  const onSupportingFileChange = (e) => {
    const fs = Array.from(e.target.files || []);
    setSupportingFiles((prev) => [...prev, ...fs]);
  };
  const removeSupportingFile = (idx) =>
    setSupportingFiles(supportingFiles.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!site) return toast.error("Select a site");
    if (!category) return toast.error("Select a category");
    if (!vendorId) return toast.error("Select a vendor");
    if (bulk) {
      const validItems = items.filter((i) => i.description.trim() && Number(i.amount) > 0);
      if (validItems.length === 0) return toast.error("Add at least one item with description and amount");
    } else {
      if (!amount || Number(amount) <= 0) return toast.error("Enter amount");
    }
    setSubmitting(true);
    try {
      const payload = {
        site, category, vendor_id: vendorId,
        notes: notes || null,
      };
      if (requestedMode) payload.requested_payment_mode = requestedMode;
      if (requestedMode === "Employee Account") {
        if (!employeeName.trim()) { setSubmitting(false); return toast.error("Employee name is required when paying to Employee Account"); }
        payload.employee_name = employeeName.trim();
      }
      if (canConfidential && confidential) payload.confidential = true;
      if (bulk) {
        payload.items = items
          .filter((i) => i.description.trim() && Number(i.amount) > 0)
          .map((i) => ({ description: i.description.trim(), amount: Number(i.amount) }));
      } else {
        payload.amount = Number(amount);
      }
      if (purchaseTime) payload.purchase_time = new Date(purchaseTime).toISOString();

      let res;
      try {
        res = await api.post("/requests", payload);
      } catch (err) {
        // Phase C3 — if server rejected purchase_time as beyond window and
        // the caller is an admin, prompt for a reason and retry with override.
        const detail = err?.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.code === "backdate_beyond_window" && user?.role === "admin") {
          const reason = window.prompt(
            `Purchase time is ${detail.days_beyond_window} days beyond the back-date window.\n\nAdmin override reason (required — audit-logged):`
          );
          if (!reason || !reason.trim()) throw err;
          res = await api.post("/requests", {
            ...payload,
            admin_override: true,
            override_reason: reason.trim(),
          });
        } else {
          throw err;
        }
      }
      const reqId = res.data.request_id;

      // Upload supporting docs (one by one)
      for (const f of supportingFiles) {
        const form = new FormData();
        form.append("file", f);
        try {
          await api.post(`/requests/${reqId}/supporting-docs`, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (e) {
          toast.warning(`Failed to attach ${f.name}`);
        }
      }

      toast.success(
        res.data?.auto_approved_cash
          ? `Cash expense recorded: ${reqId}`
          : `Request submitted: ${reqId}`
      );
      navigate("/dashboard");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" ? (detail.message || detail.code) : detail) || "Failed to submit";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredVendors = vendors.filter((v) =>
    !vendorSearch || v.name.toLowerCase().includes(vendorSearch.toLowerCase())
  );

  const createVendor = async () => {
    if (!newVendorName.trim()) return;
    try {
      const res = await api.post("/vendors", { name: newVendorName.trim() });
      setVendors([res.data, ...vendors]);
      setVendorId(res.data.vendor_id);
      setVendorName(res.data.name);
      setCreateVendorOpen(false);
      setNewVendorName("");
      setVendorOpen(false);
      toast.success("Vendor added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add vendor");
    }
  };

  const t = tier(effectiveAmount);
  const isPrivilegedForVendor = user?.role === "admin" || !!user?.permissions_effective?.manage_vendors || !!user?.permissions_effective?.approve_vendors;

  // Phase C0 — mirror the server's auto-approve rule so the UX can preview it.
  // Same 4 conditions the backend enforces; server remains the source of truth.
  const willAutoApprove = (
    cashRules?.has_permission === true
    && requestedMode === "Cash"
    && (cashRules?.allowed_categories || []).includes(category)
    && effectiveAmount > 0
    && effectiveAmount <= (cashRules?.amount_cap || 0)
    && !confidential
  );

  return (
    <AppShell back="/dashboard" title="NEW REQUEST">
      <div className="px-5 lg:px-10 py-5 lg:py-8 space-y-6 lg:max-w-3xl lg:mx-auto">
        {/* Site */}
        <Field label="SITE">
          {isSingleSite ? (
            <div
              data-testid="field-site-locked"
              className="w-full bg-slate-50 border-b-2 border-slate-300 py-3 font-display font-bold text-lg text-slate-700 flex items-center justify-between gap-3"
            >
              <span>{site || "— No site assigned —"}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 bg-white px-2 py-1 rounded-full">Assigned</span>
            </div>
          ) : (
            <select
              data-testid="field-site"
              value={site}
              onChange={(e) => setSite(e.target.value)}
              className="w-full bg-white border-b-2 border-slate-300 py-3 font-display font-bold text-lg focus:border-black outline-none"
            >
              <option value="">— Select site —</option>
              {meta.sites.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </Field>

        {/* Category */}
        <Field label="CATEGORY">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-slate-200">
            {meta.categories.map((c) => (
              <button
                key={c}
                data-testid={`cat-${c.replace(/[\s&]+/g, "-").toLowerCase()}`}
                onClick={() => setCategory(c)}
                className={`p-3 lg:p-4 border-r border-b border-slate-200 font-mono text-[11px] uppercase tracking-widest font-bold transition-all text-left ${
                  category === c ? "bg-black text-white" : "bg-white text-black hover:bg-slate-100"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Field>

        {/* Vendor */}
        <Field label="VENDOR">
          <button
            data-testid="field-vendor"
            onClick={() => setVendorOpen(true)}
            className="w-full bg-white border-b-2 border-slate-300 py-3 font-display font-bold text-lg text-left flex justify-between items-center hover:border-black"
          >
            <span className={vendorName ? "text-black" : "text-slate-400"}>
              {vendorName || "— Search vendor —"}
            </span>
            <Search size={18} className="text-slate-400" />
          </button>
        </Field>

        {/* Bulk toggle */}
        <div className="flex justify-between items-center py-3 border-b border-slate-200">
          <div>
            <div className="label-micro flex items-center gap-1"><Layers size={11}/> BULK PURCHASE</div>
            <div className="font-mono text-xs text-slate-500 mt-1">Multiple items, one approval</div>
          </div>
          <button
            data-testid="toggle-bulk"
            onClick={() => setBulk(!bulk)}
            className={`px-4 py-2 border-2 font-mono text-xs uppercase tracking-widest font-bold transition-all ${
              bulk ? "bg-olive border-olive text-white" : "bg-white border-black text-black"
            }`}
          >
            {bulk ? "ON" : "OFF"}
          </button>
        </div>

        {/* Amount or Items */}
        {!bulk ? (
          <Field label={`AMOUNT / ${t.label} • ${t.sla}`}>
            <div className="relative border-b border-slate-200">
              <span className="absolute left-0 top-1/2 -translate-y-1/2 font-display text-3xl font-black text-slate-400">₹</span>
              <input
                data-testid="field-amount"
                type="number"
                inputMode="decimal"
                onWheel={blurOnWheel}
                onKeyDown={(e) => { if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); }}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                className="w-full bg-transparent pl-8 py-4 text-5xl font-black font-display tracking-tight outline-none"
              />
            </div>
            {amount && Number(amount) > 0 && (
              <div className={`mt-1 label-micro ${t.color}`}>TIER: {t.label} / {t.sla}</div>
            )}
          </Field>
        ) : (
          <Field label={`ITEMS / ${t.label} • ${t.sla}`}>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-white border border-slate-300 px-2 py-2">
                  <Input
                    data-testid={`item-desc-${idx}`}
                    value={it.description}
                    onChange={(e) => updateItem(idx, "description", e.target.value)}
                    placeholder="Item / description"
                    className="flex-1  border-0 focus-visible:ring-0 px-1 font-mono text-sm h-9"
                  />
                  <Input
                    data-testid={`item-amount-${idx}`}
                    type="number"
                    inputMode="decimal"
                    onWheel={blurOnWheel}
                    onKeyDown={(e) => { if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); }}
                    value={it.amount}
                    onChange={(e) => updateItem(idx, "amount", e.target.value)}
                    placeholder="₹"
                    className="w-28  border-0 border-l border-slate-200 focus-visible:ring-0 font-display font-bold h-9"
                  />
                  <button
                    data-testid={`item-remove-${idx}`}
                    onClick={() => removeItem(idx)}
                    className="text-slate-400 hover:text-red-600 px-1"
                    aria-label="Remove item"
                  >
                    <X size={14}/>
                  </button>
                </div>
              ))}
              <button
                data-testid="item-add"
                onClick={addItem}
                className="w-full border-2 border-dashed border-slate-300 py-2 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-widest hover:border-black hover:bg-slate-50"
              >
                <Plus size={14}/> Add Item
              </button>
              <div className="flex justify-between items-center pt-2 border-t border-slate-200 mt-3">
                <span className="label-micro">TOTAL</span>
                <span className="font-semibold text-3xl tracking-tight">{formatINR(totalBulk)}</span>
              </div>
            </div>
          </Field>
        )}

        {/* v2.12 — Urgency / Scheduled-for / Already-purchased fields removed. */}

        {/* Requested Payment Mode — Accounts confirms the actual mode at payment time. */}
        <Field label="REQUESTED PAYMENT MODE">
          <div className="grid grid-cols-3 gap-2">
            {["Cash", "Vendor Account", "Employee Account"].map((m) => (
              <button
                key={m}
                data-testid={`requested-mode-${m.replace(/ /g, "-")}`}
                onClick={() => setRequestedMode(requestedMode === m ? "" : m)}
                className={`px-3 py-2 border-2 font-mono text-[11px] uppercase tracking-widest font-bold transition-all ${
                  requestedMode === m ? "bg-black text-white border-black" : "bg-white text-black border-slate-300 hover:border-black"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          {requestedMode === "Employee Account" && (
            <div className="mt-3">
              <input
                data-testid="field-employee-name"
                type="text"
                placeholder="Employee full name (required)"
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
                className="w-full bg-white border-b-2 border-slate-300 py-3 font-mono text-sm focus:border-black outline-none"
              />
              <div className="mt-1 label-micro">SHOWS IN REPORTS &amp; BANK LEDGER</div>
            </div>
          )}
        </Field>

        {/* v2.12 — Scheduled-for and Already-purchased removed. */}

        {/* Supporting Documents (optional) */}
        <Field label="SUPPORTING DOCUMENTS (OPTIONAL)">
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            className="hidden"
            onChange={onSupportingFileChange}
            data-testid="supporting-files-input"
          />
          <button
            data-testid="add-supporting-doc-btn"
            onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-slate-300 py-3 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-widest hover:border-black hover:bg-slate-50"
          >
            <Paperclip size={14}/> Attach Files
          </button>
          {supportingFiles.length > 0 && (
            <div className="mt-2 space-y-1">
              {supportingFiles.map((f, i) => (
                <div key={i} className="flex items-center justify-between bg-white border border-slate-200 px-3 py-2">
                  <span className="font-mono text-xs truncate">{f.name}</span>
                  <button
                    data-testid={`supporting-remove-${i}`}
                    onClick={() => removeSupportingFile(i)}
                    className="text-slate-400 hover:text-red-600"
                  >
                    <X size={14}/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </Field>

        {/* Notes */}
        <Field label="NOTES (OPTIONAL)">
          <Textarea
            data-testid="field-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="One-line note"
            rows={2}
            className="font-mono text-sm border-0 border-b-2 border-slate-300  focus-visible:ring-0 focus:border-black px-0"
          />
        </Field>

        <DuplicateWarningCard matches={dupCheck.matches} />

        {canConfidential && (
          <ConfidentialToggleCard
            checked={confidential}
            onToggle={() => setConfidential((v) => !v)}
          />
        )}

        {willAutoApprove && (
          <div
            data-testid="cash-auto-approve-banner"
            className="rounded-2xl border border-olive/30 bg-olive/5 p-4 flex items-start gap-3"
          >
            <Check size={16} strokeWidth={2.5} className="text-olive shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="font-mono text-[11px] uppercase tracking-widest text-olive font-bold">
                AUTO-APPROVED CASH · Skips approval
              </div>
              <div className="text-xs text-slate-700 mt-1 leading-relaxed">
                Payment mode Cash, amount ≤ ₹{Number(cashRules.amount_cap).toLocaleString("en-IN")},
                and category <span className="font-semibold">{category}</span> is whitelisted.
                On submit this will be recorded as <span className="font-semibold">paid</span> immediately
                and flow into category and cash-in-hand totals.
              </div>
            </div>
          </div>
        )}

        <button
          data-testid="submit-request-btn"
          onClick={submit}
          disabled={submitting}
          className="w-full bg-black text-white py-5 px-6 font-mono text-sm uppercase tracking-[0.25em] font-bold border border-slate-200 hover:bg-olive hover:border-olive transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Check size={18} strokeWidth={2.5} /> {submitting ? "SUBMITTING…" : "SUBMIT REQUEST"}
        </button>
      </div>

      <Dialog open={vendorOpen} onOpenChange={setVendorOpen}>
        <DialogContent className="max-w-md  border border-slate-200 p-0 gap-0">
          <DialogHeader className="px-5 py-4 border-b border-slate-200">
            <DialogTitle className="font-semibold tracking-tight uppercase">Select Vendor</DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-3 max-h-[60vh] overflow-auto">
            <Input
              data-testid="vendor-search"
              placeholder="Search vendor..."
              value={vendorSearch}
              onChange={(e) => setVendorSearch(e.target.value)}
              className=" border-b-2 border-t-0 border-l-0 border-r-0 border-slate-300 focus-visible:ring-0 focus:border-black px-0"
            />
            {filteredVendors.length === 0 && (
              <div className="py-6 text-center font-mono text-xs text-slate-400 uppercase">No vendors found</div>
            )}
            {filteredVendors.map((v) => (
              <button
                key={v.vendor_id}
                data-testid={`vendor-option-${v.vendor_id}`}
                onClick={() => {
                  setVendorId(v.vendor_id);
                  setVendorName(v.name);
                  setVendorOpen(false);
                  setVendorSearch("");
                }}
                className="w-full text-left px-3 py-3 border border-slate-200 hover:border-black hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-sm">{v.name}</span>
                  {v.is_new && (
                    <span data-testid={`vendor-new-tag-${v.vendor_id}`} className="px-1.5 py-0.5 bg-olive text-white font-mono text-[9px] uppercase tracking-widest font-bold">NEW</span>
                  )}
                  {v.pending_approval && (
                    <span className="px-1.5 py-0.5 bg-amber-500 text-white font-mono text-[9px] uppercase tracking-widest font-bold">PENDING</span>
                  )}
                </div>
                {v.category && <div className="label-micro mt-0.5">{v.category}</div>}
              </button>
            ))}
            <button
              data-testid="add-vendor-btn"
              onClick={() => { setNewVendorName(vendorSearch); setCreateVendorOpen(true); }}
              className="w-full border-2 border-dashed border-slate-300 py-3 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-widest hover:border-black hover:bg-slate-50"
            >
              <Plus size={14} /> {isPrivilegedForVendor ? "Add Vendor" : "Request New Vendor"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={createVendorOpen} onOpenChange={setCreateVendorOpen}>
        <DialogContent className="max-w-md  border border-slate-200 p-0 gap-0">
          <DialogHeader className="px-5 py-4 border-b border-slate-200">
            <DialogTitle className="font-semibold tracking-tight uppercase">
              {isPrivilegedForVendor ? "New Vendor" : "Request New Vendor"}
            </DialogTitle>
          </DialogHeader>
          <div className="p-5 space-y-4">
            {!isPrivilegedForVendor && (
              <div className="bg-amber-50 border border-amber-300 px-3 py-2 font-mono text-[11px] text-amber-800 leading-relaxed">
                Vendor will be created as <span className="font-bold">PENDING</span>. Admin/Accounts will approve before it can be used in requests.
              </div>
            )}
            <Input
              data-testid="new-vendor-name"
              placeholder="Vendor name"
              value={newVendorName}
              onChange={(e) => setNewVendorName(e.target.value)}
              className=" border-b-2 border-t-0 border-l-0 border-r-0 focus-visible:ring-0 focus:border-black px-0"
            />
            <button
              data-testid="create-vendor-submit"
              onClick={createVendor}
              className="w-full bg-black text-white py-3 font-mono text-xs uppercase tracking-widest font-bold hover:bg-olive"
            >
              {isPrivilegedForVendor ? "Create Vendor" : "Submit Request"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="label-micro mb-2">{label}</div>
    {children}
  </div>
);


// Phase F2 — inline amber warning card. Non-blocking; user can still submit.
function DuplicateWarningCard({ matches }) {
  if (!matches || matches.length === 0) return null;
  return (
    <div
      data-testid="duplicate-warning-card"
      className="rounded-2xl border border-amber-300 bg-amber-50/70 p-4 lg:p-5 shadow-sm"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="shrink-0 w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
          <AlertTriangle size={18} />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-amber-900 text-sm" data-testid="duplicate-warning-title">
            {matches.length === 1 ? "Possible duplicate found" : `${matches.length} possible duplicates found`}
          </div>
          <div className="text-xs text-amber-800/80 mt-0.5">
            Same vendor + category + amount within 5%, in the last 7 days. You can still submit.
          </div>
        </div>
      </div>
      <div className="space-y-2" data-testid="duplicate-warning-list">
        {matches.slice(0, 5).map((m, idx) => (
          <DuplicateMatchRow key={m.request_id} match={m} highlight={idx === 0} />
        ))}
      </div>
    </div>
  );
}

function DuplicateMatchRow({ match, highlight }) {
  const days = (() => {
    try {
      const d = new Date(match.created_at);
      const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
      if (diff <= 0) return "today";
      if (diff === 1) return "1 day ago";
      return `${diff} days ago`;
    } catch { return ""; }
  })();
  const amt = Math.round(match.approved_amount ?? match.amount ?? 0);
  return (
    <a
      href={`/requests/${match.request_id}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`duplicate-row-${match.request_id}`}
      className={`block rounded-xl border px-3 py-2.5 transition group ${
        highlight
          ? "border-amber-400 bg-white ring-1 ring-amber-200"
          : "border-amber-200/70 bg-white/70 hover:bg-white"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-900 truncate">{match.request_id}</span>
            <span className="font-semibold text-sm text-olive">₹{amt.toLocaleString("en-IN")}</span>
            {highlight && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">
                Strongest match
              </span>
            )}
            {match.same_site && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-olive/10 text-olive px-1.5 py-0.5 rounded">
                Same site
              </span>
            )}
          </div>
          <div className="text-xs text-slate-600 mt-0.5 truncate">
            {match.vendor_name || "—"} · {match.category} · {match.site} · {match.status} {days ? `· ${days}` : ""}
          </div>
        </div>
        <ExternalLink size={14} className="shrink-0 text-slate-400 group-hover:text-slate-600" />
      </div>
    </a>
  );
}

// Phase F3 — confidential toggle (visible only to view_confidential holders).
function ConfidentialToggleCard({ checked, onToggle }) {
  return (
    <div
      data-testid="confidential-toggle-card"
      className={`rounded-2xl border p-4 lg:p-5 transition ${
        checked ? "border-slate-900 bg-slate-900 text-white shadow-md" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
            checked ? "bg-white/10 text-white" : "bg-slate-100 text-slate-500"
          }`}
        >
          <Lock size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div className="font-semibold text-sm tracking-tight">
              Mark confidential
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={checked}
              data-testid="confidential-toggle"
              onClick={onToggle}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
                checked ? "bg-white" : "bg-slate-300"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full transition ${
                  checked ? "translate-x-5 bg-slate-900" : "translate-x-0.5 bg-white"
                }`}
              />
            </button>
          </div>
          <div className={`text-xs mt-1 leading-relaxed ${checked ? "text-white/70" : "text-slate-500"}`}>
            {checked
              ? "This expense will be recorded directly (auto-approved) and hidden from every normal report, chart, and search — only you and users with the confidential permission will see it."
              : "Optional. Records this expense privately, skipping the approval queue and every normal report."}
          </div>
        </div>
      </div>
    </div>
  );
}

